import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList, categoryLabels } from '@/mocks/manga';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);

  useEffect(() => {
    if (manga) {
      document.title = `${manga.title} - 星星动漫网 | ${manga.title}免费在线阅读`;
    } else {
      document.title = '漫画未找到 - 星星动漫网';
    }
  }, [manga]);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-block px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${categoryLabels[manga.category]}漫画`, to: `/category/${manga.category}` },
    { label: manga.title },
  ];

  const relatedManga = mangaList
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 5);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 星星动漫网免费在线阅读`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: '2024-01-01',
    dateModified: manga.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '星星动漫网',
      url: 'https://www.xingxingdongman.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.xingxingdongman.com/article/${manga.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.to ? `https://www.xingxingdongman.com${item.to}` : undefined,
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <Navbar />

      <main className="min-h-screen bg-background-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="py-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[300px] shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden sticky top-24">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 星星动漫网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700">
                    {categoryLabels[manga.category]}
                  </span>
                  <span className={`px-2.5 py-1 rounded text-xs font-medium ${
                    manga.status === '连载中'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}>
                    {manga.status}
                  </span>
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded text-xs bg-background-100 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-6">
                  <span>作者：<strong>{manga.author}</strong></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <strong>{manga.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    <strong>{manga.views >= 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views}</strong> 次阅读
                  </span>
                  <span>共 <strong>{manga.chapters.length}</strong> 话</span>
                  <span>更新至 <strong>{manga.updatedAt}</strong></span>
                </div>

                <div className="bg-background-100/60 rounded-lg p-5 mb-8">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{manga.description}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-semibold text-foreground-900">章节列表</h2>
                    <div className="flex items-center gap-2">
                      <button className="whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 cursor-pointer">
                        正序
                      </button>
                      <button className="whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 cursor-pointer">
                        倒序
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[400px] overflow-y-auto pr-1">
                    {manga.chapters.map((ch) => (
                      <a
                        key={ch.id}
                        href="#"
                        className="block px-3 py-2 rounded-md bg-background-100 hover:bg-background-200 text-sm text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis"
                        onClick={(e) => e.preventDefault()}
                      >
                        {ch.title}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
                    开始阅读第一话
                  </button>
                  <button className="whitespace-nowrap w-12 h-12 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="whitespace-nowrap w-12 h-12 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedManga.length > 0 && (
            <section className="pb-16" aria-labelledby="related-title">
              <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/article/${rm.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rm.cover}
                        alt={rm.title}
                        title={`${rm.title} - 星星动漫网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {rm.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rm.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}